import { useState, useEffect, FormEvent } from 'react'
import { listUsers, createUser, updateUser, deleteUser, changePassword, resetUserPassword, AuthUser, UserRole } from '../services/auth'
import { useAuth } from '../context/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'

/**
 * H4 del roadmap (Fase B #5) + panel de administrador (a petición del
 * usuario): "poder compartirlo con Alfonso/Elizabeth/Sylwester" — esta
 * página es la forma de darles acceso de verdad, con permisos
 * diferenciados (admin/operador), sin necesitar curl ni tocar la base de
 * datos a mano. Solo accesible para role === 'admin' (ver App.tsx /
 * ProtectedRoute adminOnly) — el backend lo exige igualmente.
 */
const Users = () => {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState<AuthUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<UserRole>('operator')
  const [formError, setFormError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)

  // Restablecer contraseña ajena: solo una fila abierta a la vez.
  const [resetRowId, setResetRowId] = useState<number | null>(null)
  const [resetValue, setResetValue] = useState('')
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetSaving, setResetSaving] = useState(false)

  // Editar el rol de otro usuario ya existente (el CRUD que faltaba: antes
  // solo se podía fijar el rol al crearlo, no cambiarlo después).
  const [roleUpdatingId, setRoleUpdatingId] = useState<number | null>(null)
  const [roleError, setRoleError] = useState<{ id: number; message: string } | null>(null)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const res = await listUsers()
      setUsers(res.data)
      setError(null)
    } catch (err) {
      setError('Error al cargar la lista de usuarios')
      console.error('Error fetching users:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)

    if (password.length < 8) {
      setFormError('La contraseña debe tener al menos 8 caracteres')
      return
    }

    setCreating(true)
    try {
      const res = await createUser(email, password, name || undefined, role)
      setUsers((prev) => [...prev, res.data])
      setName('')
      setEmail('')
      setPassword('')
      setRole('operator')
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'Error al crear el usuario')
      console.error('Error creando usuario:', err)
    } finally {
      setCreating(false)
    }
  }

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault()
    setPasswordError(null)
    setPasswordSuccess(false)

    if (newPassword.length < 8) {
      setPasswordError('La nueva contraseña debe tener al menos 8 caracteres')
      return
    }

    setChangingPassword(true)
    try {
      await changePassword(currentPassword, newPassword)
      setPasswordSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
    } catch (err: any) {
      setPasswordError(err?.response?.data?.message || 'Contraseña actual incorrecta')
      console.error('Error cambiando contraseña:', err)
    } finally {
      setChangingPassword(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Seguro que quieres eliminar el acceso de este usuario?')) {
      return
    }
    try {
      await deleteUser(id)
      setUsers((prev) => prev.filter((u) => u.id !== id))
    } catch (err) {
      console.error('Error eliminando usuario:', err)
      alert('No se pudo eliminar el usuario')
    }
  }

  const handleRoleChange = async (id: number, newRole: UserRole) => {
    setRoleError(null)
    setRoleUpdatingId(id)
    try {
      const res = await updateUser(id, { role: newRole })
      setUsers((prev) => prev.map((u) => (u.id === id ? res.data : u)))
    } catch (err: any) {
      // No se toca el estado: al ser un <select> controlado por u.role, si
      // la llamada falla el valor mostrado vuelve solo al que tenía antes.
      setRoleError({ id, message: err?.response?.data?.message || 'No se pudo cambiar el rol' })
      console.error('Error cambiando el rol:', err)
    } finally {
      setRoleUpdatingId(null)
    }
  }

  const openResetPassword = (id: number) => {
    setResetRowId(id)
    setResetValue('')
    setResetError(null)
  }

  const handleResetPassword = async (id: number) => {
    if (resetValue.length < 8) {
      setResetError('Debe tener al menos 8 caracteres')
      return
    }
    setResetSaving(true)
    setResetError(null)
    try {
      await resetUserPassword(id, resetValue)
      setResetRowId(null)
      setResetValue('')
    } catch (err) {
      console.error('Error restableciendo contraseña:', err)
      setResetError('No se pudo restablecer la contraseña')
    } finally {
      setResetSaving(false)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
          <span className="mr-3">👥</span>
          USUARIOS
        </h1>
        <p className="text-dashboard-muted mt-1">
          Da acceso al dashboard a Alfonso, Elizabeth, Sylwester o quien más lo necesite
        </p>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-bold text-dashboard-text mb-4">Cambiar mi contraseña</h2>
        <form onSubmit={handleChangePassword} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label htmlFor="current-password" className="block text-sm font-medium text-dashboard-text mb-2">
              Contraseña actual
            </label>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="text-input"
            />
          </div>
          <div>
            <label htmlFor="new-own-password" className="block text-sm font-medium text-dashboard-text mb-2">
              Nueva contraseña
            </label>
            <input
              id="new-own-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="text-input"
            />
          </div>
          <button type="submit" disabled={changingPassword} className="btn-secondary disabled:opacity-50">
            {changingPassword ? 'Guardando…' : 'Cambiar contraseña'}
          </button>
        </form>
        {passwordError && <div className="text-sm text-red-600 mt-3">{passwordError}</div>}
        {passwordSuccess && <div className="text-sm text-green-600 mt-3">Contraseña actualizada correctamente</div>}
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-bold text-dashboard-text mb-4">Añadir usuario</h2>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <label htmlFor="new-name" className="block text-sm font-medium text-dashboard-text mb-2">
              Nombre
            </label>
            <input
              id="new-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="text-input"
            />
          </div>
          <div>
            <label htmlFor="new-email" className="block text-sm font-medium text-dashboard-text mb-2">
              Email
            </label>
            <input
              id="new-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="text-input"
            />
          </div>
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-dashboard-text mb-2">
              Contraseña
            </label>
            <input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="text-input"
            />
          </div>
          <div>
            <label htmlFor="new-role" className="block text-sm font-medium text-dashboard-text mb-2">
              Rol
            </label>
            <select
              id="new-role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="filter-select w-full"
            >
              <option value="operator">Operador</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <button type="submit" disabled={creating} className="btn-primary disabled:opacity-50">
            {creating ? 'Creando…' : 'Crear usuario'}
          </button>
        </form>
        {formError && <div className="text-sm text-red-600 mt-3">{formError}</div>}
      </div>

      <div className="card">
        <div className="p-6 border-b border-dashboard-border">
          <h2 className="text-xl font-bold text-dashboard-text">Usuarios con acceso</h2>
        </div>

        {error ? (
          <div className="p-6 text-center text-dashboard-muted">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="font-semibold">{u.name || '—'}</td>
                    <td>{u.email}</td>
                    <td>
                      {u.id === currentUser?.id ? (
                        <span className={`badge ${u.role === 'admin' ? 'badge-premium' : 'badge-info'}`}>
                          {u.role === 'admin' ? 'Administrador' : 'Operador'}
                        </span>
                      ) : (
                        <div>
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.id, e.target.value as UserRole)}
                            disabled={roleUpdatingId === u.id}
                            aria-label={`Rol de ${u.email}`}
                            className="text-input py-1 text-sm w-40 disabled:opacity-50"
                          >
                            <option value="operator">Operador</option>
                            <option value="admin">Administrador</option>
                          </select>
                          {roleError?.id === u.id && (
                            <div className="text-xs text-red-600 mt-1">{roleError.message}</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-right">
                      {u.id === currentUser?.id ? (
                        <span className="text-xs text-dashboard-muted">Tu cuenta</span>
                      ) : resetRowId === u.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <input
                            type="password"
                            value={resetValue}
                            onChange={(e) => setResetValue(e.target.value)}
                            placeholder="Nueva contraseña"
                            minLength={8}
                            aria-label={`Nueva contraseña para ${u.email}`}
                            className="text-input w-40 py-1.5 text-sm"
                          />
                          <button
                            onClick={() => handleResetPassword(u.id)}
                            disabled={resetSaving}
                            className="text-sm text-globo-700 hover:text-globo-900 font-medium disabled:opacity-50"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => setResetRowId(null)}
                            className="text-sm text-dashboard-muted hover:text-dashboard-text font-medium"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-4">
                          <button
                            onClick={() => openResetPassword(u.id)}
                            className="text-sm text-globo-700 hover:text-globo-900 font-medium"
                          >
                            Restablecer contraseña
                          </button>
                          <button
                            onClick={() => handleDelete(u.id)}
                            className="text-sm text-red-600 hover:text-red-800 font-medium"
                          >
                            Eliminar acceso
                          </button>
                        </div>
                      )}
                      {resetRowId === u.id && resetError && (
                        <div className="text-xs text-red-600 mt-1">{resetError}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default Users
